<script setup lang="ts">
import { computed } from 'vue';
import type { PrintingQueueItem } from './usePrintingFlow';

const props = defineProps<{
  centerError: boolean;
  isLandscapeKiosk: boolean;
  isVerticalKiosk: boolean;
  portrait: PrintingQueueItem;
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const partialCounts = computed(() => {
  const { count, isPartial, printedCopies } = props.portrait;
  if (!(isPartial && printedCopies && count)) {
    return null;
  }
  return { printed: printedCopies, total: count };
});
</script>

<template>
  <i
    v-if="portrait.isNotStarted || portrait.isPrinting"
    class="Printing__icon Printing__icon--printing kiosk-spinner"
    :class="{
      'Printing__icon--landscape-centered': isLandscapeKiosk,
      'Printing__icon--vertical-hidden': isVerticalKiosk,
    }"
  />
  <div
    v-if="portrait.isPrinting && portrait.printSubstate"
    class="Printing__substate"
    :class="{ 'Printing__substate--centered': centerError }"
    data-testid="printing-item-substate"
  >
    {{
      portrait.printSubstate === 'retrying'
        ? $t('Printing.retrying')
        : $t('Printing.waiting')
    }}
  </div>
  <i
    v-if="portrait.isPrinted"
    class="Printing__icon Printing__icon--success kiosk-success-icon"
    data-testid="printing-item-success"
  />
  <i
    v-if="portrait.isError || portrait.isPartial"
    class="Printing__icon Printing__icon--error"
    data-testid="printing-item-error"
  />

  <div
    v-if="portrait.isError || portrait.isPartial"
    class="Printing__error"
    :class="{ 'Printing__error--centered': centerError }"
  >
    {{
      partialCounts
        ? $t('Printing.errorPartial', partialCounts)
        : $t('Printing.error')
    }}
  </div>
</template>

<style scoped lang="scss">
.Printing__icon {
  --Printing__icon-size: calc(var(--ImageGallery--text-size) * 2);
  --Printing__icon-offset: calc(var(--ImageGallery--text-size) * 0.95);

  position: absolute;
  width: var(--Printing__icon-size);
  height: var(--Printing__icon-size);
  top: var(--Printing__icon-offset);
  right: var(--Printing__icon-offset);
  z-index: 2;
}

.Printing__icon--printing {
  --kiosk-spinner-size: calc(var(--Printing__icon-size) * 0.55);
  --kiosk-spinner-color: #ffffff;
  --kiosk-spinner-opacity: 0.8;
  --kiosk-spinner-thinkness: 0.17;
}

.Printing__icon--printing.Printing__icon--landscape-centered {
  top: 50%;
  right: auto;
  left: 50%;
  transform: translate(-50%, -50%);
}

.Printing__icon--printing.Printing__icon--vertical-hidden {
  display: none;
}

.Printing__icon--error {
  background: #dc2726;
  box-shadow: 0 calc(var(--ImageGallery--text-size) * 0.12)
    calc(var(--ImageGallery--text-size) * 0.24) 0 rgba(0, 0, 0, 0.2);
  border-radius: 100%;
}

.Printing__icon--error::after {
  content: '';
  position: absolute;
  inset: 0;
  background: #ffffff;
  mask-image: url('../../../assets/images/gallery-error.svg');
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: 45%;
}

.Printing__error {
  --Printing__error-gap: var(--ImageGallery--text-size);

  border-radius: var(--ImageGallery--item-radius);
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(
    to bottom,
    rgb(96 96 96 / 12%) 0%,
    rgb(96 96 96 / 12%) 52%,
    rgb(96 96 96 / 20%) 64%,
    rgb(72 72 72 / 76%) 100%
  );
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  text-align: center;
  color: #ffffff;
  padding: var(--Printing__error-gap);
}

.Printing__substate {
  --Printing__substate-gap: var(--ImageGallery--text-size);

  border-radius: var(--ImageGallery--item-radius);
  position: absolute;
  inset: auto 0 0;
  padding: var(--Printing__substate-gap);
  background: linear-gradient(to bottom, rgb(48 48 48 / 0%) 0%, rgb(48 48 48 / 64%) 100%);
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  text-align: center;
  color: #ffffff;
  pointer-events: none;
}

.Printing__substate--centered {
  --Printing__substate-gap: 1.2rem;

  font-size: 1.4rem;
  line-height: 1.1;
  overflow-wrap: break-word;
}

.Printing__error--centered {
  --Printing__error-gap: 1.2rem;

  align-items: center;
  justify-content: center;
  background: rgb(72 72 72 / 48%);
  font-size: 1.8rem;
  line-height: 1.08;
  overflow-wrap: break-word;
}
</style>
